import { TFunction } from 'react-i18next'

import type { ObjectId, ShowcaseData, ShowcaseMotivation, StoryPattern, TermRef } from '../types'
import { formatDelta } from './scoreDisplay'
import type { UATaxonomyLabels } from './taxonomyLabels'

/**
 * Theme palette token for a month-over-month delta. An absent delta (nothing
 * to compare with) has no trend, so it reads as secondary text rather than a
 * rise or a fall.
 */
export const deltaColor = (deltaPp: number | undefined): 'success.main' | 'error.main' | 'text.secondary' =>
  deltaPp === undefined ? 'text.secondary' : deltaPp >= 0 ? 'success.main' : 'error.main'

/** Clamp a popularity/percentage value to the 0-100 bar range. */
export const clampPct = (value: number): number => Math.max(0, Math.min(100, value))

/** Round to one decimal place. */
const round1 = (value: number): number => Math.round(value * 10) / 10

/**
 * Top rising story patterns for the overview cards — parents only, sorted by
 * their delta (largest increase first). Patterns without a delta have no trend
 * and are left out.
 */
export const getTopRisingPatterns = (patterns: StoryPattern[], limit = 3): StoryPattern[] =>
  patterns
    .filter((p) => p.deltaPp !== undefined && p.deltaPp > 0)
    .sort((a, b) => (b.deltaPp ?? 0) - (a.deltaPp ?? 0))
    .slice(0, limit)

/**
 * Gap, in percentage points, between a pattern's own Monthly Δ and another
 * series' Monthly Δ, rounded to one decimal. Absent when either side has no
 * delta, since there is no trend to compare.
 */
export const monthlyDeltaGap = (deltaPp: number | undefined, otherDeltaPp: number | undefined): number | undefined =>
  deltaPp === undefined || otherDeltaPp === undefined ? undefined : round1(deltaPp - otherDeltaPp)

/**
 * Differentiation delta for a benchmarked story pattern: the game's
 * month-over-month delta minus the benchmark's, in percentage points. Surfaces
 * patterns whose monthly momentum diverges most between the two sides. A game
 * compared against itself yields 0.
 */
export const differentiationDelta = (p: StoryPattern): number | undefined => monthlyDeltaGap(p.deltaPp, p.benchmark?.deltaPp)

/**
 * Top differentiating story patterns for the comparison overview cards —
 * parents only, ranked by the magnitude of their differentiation delta, so the
 * largest divergences surface first regardless of sign.
 */
export const getTopDifferentiatingPatterns = (patterns: StoryPattern[], limit = 3): StoryPattern[] =>
  patterns
    .filter((p) => (differentiationDelta(p) ?? 0) !== 0)
    .sort((a, b) => Math.abs(differentiationDelta(b) ?? 0) - Math.abs(differentiationDelta(a) ?? 0))
    .slice(0, limit)

/**
 * A single row of the App-vs-benchmark story-pattern mix: the game's share of
 * its own ads using a pattern (`app`), the benchmark's share (`genre`), and
 * their gap in percentage points (`gap = app − genre`). A positive gap means
 * the game over-indexes on the pattern; negative means it under-indexes (or
 * skips it entirely when `app` is 0).
 */
export interface PatternMixRow {
  id: ObjectId
  /** Display label of the pattern. */
  label: string
  /** Share of the game's ads expressing this pattern (0-100). */
  app: number
  /** Share of the benchmark's ads expressing this pattern (0-100). */
  genre: number
  /** `app − genre`, in percentage points (one decimal). */
  gap: number
}

/**
 * Build the App-vs-benchmark mix rows from a game's benchmarked parent story
 * patterns. Includes patterns the benchmark uses but the game skips (`app` 0,
 * `genre` > 0) so the chart can surface under-indexing, and drops patterns
 * neither side uses. Sorted by gap descending, so the strongest over-indexes
 * lead and the deepest under-indexes trail.
 */
export const getPatternMixRows = (patterns: StoryPattern[], labelOf: (pattern: TermRef) => string): PatternMixRow[] =>
  patterns
    .map<PatternMixRow>((p) => {
      const genre = p.benchmark?.sharePct ?? 0
      return { id: p.pattern.id, label: labelOf(p.pattern), app: p.sharePct, genre, gap: round1(p.sharePct - genre) }
    })
    .filter((row) => row.app > 0 || row.genre > 0)
    .sort((a, b) => b.gap - a.gap)

/**
 * Join the narrative sentences of a Monthly Insight / takeaways body. Latin
 * scripts separate sentences with a space, while JA/ZH run them together after
 * their full stop, so the separator is localised rather than hardcoded.
 */
const joinSentences = (t: TFunction, parts: string[]): string => parts.join(t('sentence_separator'))

/**
 * Build the "Key takeaways" sentences for the App-vs-Genre mix. A localised
 * sentence template per signal (mirroring {@link buildGameOverviewTldrText}), so
 * the copy is deterministic and translatable while the numbers/names update with
 * the selected game, month and network scope. The templates carry inline
 * `<strong>` markup the card sanitises.
 *
 * The "skips" sentence names at most two patterns and selects its template by
 * key rather than through i18next's `count` suffix: JA and ZH have a single
 * CLDR plural form, so a plural key always resolves to the two-pattern copy and
 * renders its conjunction with nothing after it.
 */
export const buildPatternMixTakeaways = (t: TFunction, gameName: string, benchmarkLabel: string, rows: PatternMixRow[]): string => {
  if (!rows.length) return ''
  const over = rows.filter((r) => r.gap > 0)
  const under = [...rows].filter((r) => r.gap < 0).sort((a, b) => a.gap - b.gap)
  const skipped = under.filter((r) => r.app === 0 && r.genre > 0)

  const parts: string[] = []
  const top = over[0]
  const bottom = under[0]
  if (top) {
    parts.push(
      t(bottom ? 'pattern_mix_takeaway_over_and_under' : 'pattern_mix_takeaway_over', {
        game: gameName,
        benchmark: benchmarkLabel,
        pattern: top.label,
        appPct: top.app,
        benchmarkPct: top.genre,
        underPattern: bottom?.label,
        underAppPct: bottom?.app,
        underBenchmarkPct: bottom?.genre,
      })
    )
  } else if (bottom) {
    parts.push(
      t('pattern_mix_takeaway_under_only', { game: gameName, benchmark: benchmarkLabel, pattern: bottom.label, appPct: bottom.app, benchmarkPct: bottom.genre })
    )
  }
  if (skipped.length) {
    parts.push(
      t(skipped.length > 1 ? 'pattern_mix_takeaway_skips_two' : 'pattern_mix_takeaway_skips_one', {
        benchmark: benchmarkLabel,
        first: skipped[0].label,
        second: skipped[1]?.label,
      })
    )
  }
  return joinSentences(t, parts)
}

/** Story patterns whose associated motivations (keyed by pattern id) include the given motivation. */
export const patternsForMotivation = (patterns: StoryPattern[], narrativeMotivations: Record<ObjectId, TermRef[]>, motivationId: ObjectId): StoryPattern[] =>
  patterns.filter((p) => (narrativeMotivations[p.pattern.id] ?? []).some((m) => m.id === motivationId))

/**
 * Resolve a pattern id to a pattern. The id may be a top-level pattern or a
 * sub-pattern; in the latter case the parent is returned too so breadcrumbs and
 * descriptions can fall back to it.
 */
export const findPattern = (patterns: StoryPattern[], patternId: ObjectId): { pattern: StoryPattern | null; parent: StoryPattern | null } => {
  const top = patterns.find((p) => p.pattern.id === patternId)
  if (top) return { pattern: top, parent: null }
  for (const parent of patterns) {
    const child = (parent.children ?? []).find((c) => c.pattern.id === patternId)
    if (child) return { pattern: child, parent }
  }
  return { pattern: null, parent: null }
}

/** Find a motivation row by motivation id. */
export const findMotivation = (motivations: ShowcaseMotivation[], motivationId: ObjectId): ShowcaseMotivation | undefined =>
  motivations.find((m) => m.motivation.id === motivationId)

/** Resolve id lists (`topCreativeIds`, `narrativeCreativeIds[…]`, coverage ids) against the creatives of the same response, keeping the list order. */
export const resolveIds = <T extends { id: ObjectId }>(ids: readonly ObjectId[] | undefined, items: readonly T[]): T[] => {
  if (!ids?.length) return []
  const byId = new Map(items.map((item) => [item.id, item]))
  return ids.map((id) => byId.get(id)).filter((item): item is T => item !== undefined)
}

/**
 * Build a human scope title for the Monthly TLDR badge from the active month
 * (genre/network scope is reflected in the surrounding UI). Falls back to a
 * generic label when no month is selected.
 */
export const buildScopeTitle = (t: TFunction, monthLabel: string | null): string => `${t('showcase_scope_games')}${monthLabel ? ` / ${monthLabel}` : ''}`

/**
 * Scope title for the game page's Monthly Insight card — the game name plus the
 * active month (e.g. "Royal Kingdom / April 2026"). Falls back to just the name
 * when no month is selected.
 */
export const buildGameScopeTitle = (gameName: string, monthLabel: string | null): string => `${gameName}${monthLabel ? ` / ${monthLabel}` : ''}`

/**
 * Attributes that vary per game/month; everything else in the sentence is fixed
 * copy. Pattern and motivation names are display copy, so callers supply them
 * already localised.
 */
export interface GameOverviewTldrInput {
  gameName: string
  /** The game's most-used story pattern + its share of the game's creatives (0-100). */
  topPattern?: { theme: string; pct: number }
  /** The strongest creative-ad motivation + its 0-5 intensity. */
  topMotivation?: { name: string; intensity: number }
  /** Rising story patterns (label + formatted delta), strongest first. */
  risingPatterns?: Array<{ theme: string; delta: string }>
  /** The biggest motivation blind spot, if the diagnosis surfaced one. */
  topBlindSpot?: string
}

/**
 * "Rising story patterns to watch" sentence shared by the market and game
 * Monthly Insight bodies. Names at most two patterns, picking the one- or
 * two-pattern template by key (see {@link buildPatternMixTakeaways}).
 */
const risingPatternsSentence = (t: TFunction, patterns: Array<{ theme: string; delta: string }>): string =>
  t(patterns.length > 1 ? 'tldr_rising_patterns_two' : 'tldr_rising_patterns_one', {
    first: patterns[0].theme,
    firstDelta: patterns[0].delta,
    second: patterns[1]?.theme,
    secondDelta: patterns[1]?.delta,
  })

/**
 * Build the game page's Monthly Insight body from the Overview's own stats. A
 * localised sentence template per signal (mirroring
 * {@link buildFallbackTldrText}), so the copy is deterministic and translatable
 * while the numbers/names update with the selected game, month and network
 * scope. The templates carry inline `<strong>` markup the card sanitises.
 */
export const buildGameOverviewTldrText = (
  t: TFunction,
  { gameName, topPattern, topMotivation, risingPatterns = [], topBlindSpot }: GameOverviewTldrInput
): string => {
  const parts: string[] = []
  if (topPattern) {
    parts.push(t('game_tldr_top_pattern', { game: gameName, pattern: topPattern.theme, pct: topPattern.pct }))
  }
  if (topMotivation) {
    parts.push(t('game_tldr_top_motivation', { motivation: topMotivation.name, intensity: topMotivation.intensity.toFixed(1) }))
  }
  if (risingPatterns.length) {
    parts.push(risingPatternsSentence(t, risingPatterns))
  }
  if (topBlindSpot) {
    parts.push(t('game_tldr_blind_spot', { motivation: topBlindSpot }))
  }
  return joinSentences(t, parts)
}

/**
 * Attributes that vary per comparison; everything else in the sentence is fixed
 * copy. Pattern and motivation names are display copy, so callers supply them
 * already localised.
 */
export interface CompareOverviewTldrInput {
  sourceName: string
  targetName: string
  /** Number of motivations both sides rate as core drivers (Creative Ads signal). */
  sharedCount: number
  /** The motivation the two sides disagree on most, if any. */
  topDiverging?: string
  /** The story pattern where the source's monthly trend diverges most from the target (label + formatted delta). */
  topDifferentiatingPattern?: { theme: string; delta: string }
  /** Number of creative tags both sides use. */
  sharedTagCount: number
}

/**
 * Build the compare dialog's Overview Monthly Insight body from the comparison's
 * own stats. A localised sentence template per signal (mirroring
 * {@link buildGameOverviewTldrText}), so the copy is deterministic and
 * translatable while the numbers/names update with the selected matchup, month
 * and network scope. The templates carry inline `<strong>` markup the card
 * sanitises.
 */
export const buildCompareOverviewTldrText = (
  t: TFunction,
  { sourceName, targetName, sharedCount, topDiverging, topDifferentiatingPattern, sharedTagCount }: CompareOverviewTldrInput
): string => {
  const parts: string[] = []
  if (sharedCount > 0) {
    parts.push(t('compare_tldr_shared_motivations', { count: sharedCount, source: sourceName, target: targetName }))
  } else {
    parts.push(t('compare_tldr_no_shared_motivations', { source: sourceName, target: targetName }))
  }
  if (topDiverging) {
    parts.push(t('compare_tldr_top_diverging', { motivation: topDiverging }))
  }
  if (topDifferentiatingPattern) {
    parts.push(
      t('compare_tldr_top_differentiating_pattern', {
        source: sourceName,
        pattern: topDifferentiatingPattern.theme,
        delta: topDifferentiatingPattern.delta,
      })
    )
  }
  if (sharedTagCount > 0) {
    parts.push(t('compare_tldr_shared_tags', { count: sharedTagCount }))
  }
  return joinSentences(t, parts)
}

/**
 * Derive a Monthly TLDR body when the backend does not supply a
 * `monthlySummary`. Uses the leading pattern, the most prevalent motivation and
 * the top rising patterns so the card always reads as data-driven.
 *
 * Unlike the other builders this one reads the names off the payload itself, so
 * the resolvers are injected rather than pre-applied by the caller. They stay
 * parameters (never module-level lookups) so the builder remains pure.
 */
export const buildFallbackTldrText = (t: TFunction, data: ShowcaseData, taxonomy: Pick<UATaxonomyLabels, 'motivationLabel' | 'patternLabel'>): string => {
  const topPattern = data.narrativePatterns[0]
  const topMotivation = data.motivations[0]
  const rising = getTopRisingPatterns(data.narrativePatterns, 2)
  const parts: string[] = []
  if (topPattern) {
    parts.push(t('showcase_tldr_top_pattern', { pattern: taxonomy.patternLabel(topPattern.pattern), pct: round1(topPattern.sharePct) }))
  }
  if (topMotivation) {
    parts.push(t('showcase_tldr_top_motivation', { motivation: taxonomy.motivationLabel(topMotivation.motivation), pct: round1(topMotivation.sharePct) }))
  }
  if (rising.length) {
    parts.push(
      risingPatternsSentence(
        t,
        rising.map((p) => ({ theme: taxonomy.patternLabel(p.pattern), delta: formatDelta(p.deltaPp) }))
      )
    )
  }
  return joinSentences(t, parts)
}
